import { createContextualCan } from '@casl/react'
import { AppAbility, defineAbilityFor, models } from '@multitec/auth'
import { ShieldOff } from 'lucide-react'
import { createContext, ReactNode, useContext } from 'react'

import { Card, CardHeader, CardTitle, Text } from '@/components'
import { useAuthStore } from '@/features/sessao/store/useAuthStore'
import { cn } from '@/lib/utils'

const AbilityContext = createContext<AppAbility>({} as AppAbility)
const DefaultCan = createContextualCan(AbilityContext.Consumer)

export function UserAbilityProvider({ children }: React.PropsWithChildren) {
  const { user } = useAuthStore()

  const userAbilities = user
    ? defineAbilityFor(
        models.user.parse({
          id: user.id,
          role: user.role,
        }),
      )
    : defineAbilityFor(
        models.user.parse({
          id: 'guest',
          role: 'GUEST',
        }),
      )

  return (
    <AbilityContext.Provider value={userAbilities}>
      {children}
    </AbilityContext.Provider>
  )
}

export function usePermissions() {
  const context = useContext(AbilityContext)
  if (!context) {
    throw new Error('useUserAbility must be used within a UserAbilityProvider')
  }
  return context
}

type CanProps = Parameters<typeof DefaultCan>[0] & {
  asDefault?: boolean
  fallback_title?: string
  fallback?: ReactNode | null
}

export function Can({
  asDefault,
  children,
  fallback,
  fallback_title,
  ...props
}: CanProps) {
  if (asDefault) return <DefaultCan {...props}>{children}</DefaultCan>

  return (
    <DefaultCan {...props} passThrough>
      {(allowed) => {
        if (allowed) return <>{children}</>

        if (fallback === null) return null

        if (fallback) return fallback

        return (
          <Card>
            {fallback_title && (
              <CardHeader>
                <CardTitle>{fallback_title}</CardTitle>
              </CardHeader>
            )}

            <div className="flex select-none flex-row items-center gap-4 lg:flex-col">
              <div
                className={cn(
                  'flex size-16 min-h-16 min-w-16 items-center justify-center rounded-full bg-slate-400/10 shadow-md',
                  'lg:size-20 lg:min-h-20 lg:min-w-20',
                )}
              >
                <ShieldOff
                  className="size-8 opacity-40 lg:size-12"
                  strokeWidth={0.5}
                />
              </div>
              <div className="text-left lg:text-center">
                <Text className="text-sm">Conteúdo indisponível</Text>
                <Text className="text-xs" variant="soft">
                  Você não tem permissão para visualizar este conteúdo.
                </Text>
              </div>
            </div>
          </Card>
        )
      }}
    </DefaultCan>
  )
}
